import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipContent, TooltipProvider, TooltipTrigger, Tooltip as TooltipUI } from '@/components/ui/tooltip';
import { useDashboard } from '@/contexts/DashboardContext';
import { GRID_CONFIG } from '@/lib/chartConfig';
import { formatSeconds, formatTrendsXAxisLabel, groupDataByRangeType } from '@/lib/utils';
import { WidgetData } from '@/types';
import { Info } from 'lucide-react';
import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

interface HandlingTimeDataPoint {
  time: string;
  periodAvgHandledCall: number;
  previousAvgHandledPeriodCall: number;
}

interface TooltipPayloadItem {
  value: number;
  name: string;
  color: string;
  dataKey: keyof HandlingTimeDataPoint;
  payload: HandlingTimeDataPoint;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  theme?: string;
  rangeType?: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
}

interface AverageHandlingTimeTrendWidgetProps {
  data: WidgetData;
  isGeneralMode?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  error?: string;
  rangeType?: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
}

const CustomTooltip = ({ active, payload, label, theme, rangeType = 'weekly' }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;

  return (
    <div className={`border rounded-lg shadow-md p-3 min-w-[180px] ${theme === "dark"
      ? "bg-gray-800 border-gray-600 text-white"
      : "bg-white border-gray-200 text-gray-900"
      }`}>
      {/* <div className="font-semibold mb-1">{formatTrendsXAxisLabel(label, rangeType)}</div> */}
      {payload.map((item) => (
        <div key={item.dataKey} className="text-sm" style={{ color: item.color }}>
          Time: {formatSeconds(Number(item.value))}
          {/* {item.name}: {formatSeconds(Number(item.value))} */}
        </div>
      ))}
    </div>
  );
};

const ChartContent = ({ data, theme, rangeType }: { data: HandlingTimeDataPoint[], theme: string, rangeType: 'weekly' | 'monthly' | 'quarterly' | 'yearly' }) => {
  // Check if all data points are 0 or null/undefined
  const isAllDataZero = data?.every(item =>
    (item.periodAvgHandledCall === 0 || item.periodAvgHandledCall == null) &&
    (item.previousAvgHandledPeriodCall === 0 || item.previousAvgHandledPeriodCall == null)
  ) ?? false;

  return (
    <ResponsiveContainer width="99%" height={280}>
      <LineChart data={data} margin={{ left: 20, right: 30, top: 20, bottom: 10 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="time"
          tickFormatter={(label) => formatTrendsXAxisLabel(label, rangeType as 'weekly' | 'monthly' | 'quarterly' | 'yearly')}
          tick={{ fontSize: 14, fill: theme === "dark" ? '#fff' : '#6b7280' }}
        />
        <YAxis
          tick={{ fontSize: 14, fill: theme === "dark" ? '#fff' : '#6b7280' }}
          tickFormatter={formatSeconds}
          domain={isAllDataZero ? [0, 180] : [0, 'dataMax']}
          ticks={isAllDataZero ? [0, 30, 60, 90, 120, 150, 180] : undefined}
        />
        <Tooltip
          content={<CustomTooltip theme={theme} rangeType={rangeType} />}
          cursor={{ stroke: theme === "dark" ? '#4b5563' : '#d1d5db' }}
        />
        {!isAllDataZero && (
          <>
            <Line
              type="monotone"
              dataKey="periodAvgHandledCall"
              name=""
              stroke={theme === "dark" ? '#1681F6' : '#23407a'}
              strokeWidth={2}
              dot={{ r: 5, stroke: theme === "dark" ? '#1681F6' : '#23407a', strokeWidth: 2, fill: theme === "dark" ? '#0f172a' : 'white' }}
              activeDot={{ r: 7, fill: theme === "dark" ? '#1681F6' : '#23407a', stroke: theme === "dark" ? '#1681F6' : '#23407a', strokeWidth: 2 }}
            />
            <Line
              type="monotone"
              dataKey="previousAvgHandledPeriodCall"
              name=""
              stroke={theme === "dark" ? '#00FEC1' : '#6ed6e9'}
              strokeWidth={2}
              dot={{ r: 5, stroke: theme === "dark" ? '#00FEC1' : '#6ed6e9', strokeWidth: 2, fill: theme === "dark" ? '#0f172a' : 'white' }}
              activeDot={{ r: 7, fill: theme === "dark" ? '#00FEC1' : '#6ed6e9', stroke: theme === "dark" ? '#00FEC1' : '#6ed6e9', strokeWidth: 2 }}
            />
          </>
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

const WidgetHeader = () => (
  <CardHeader>
    <div className="flex items-center gap-2">
      <span>Average Handle Time Trend</span>
      <TooltipProvider>
        <TooltipUI delayDuration={0}>
          <TooltipTrigger asChild>
            <span className="cursor-pointer">
              <Info className="h-4 w-4 text-muted-foreground" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="max-w-xs text-xs font-normal">
              The pattern of average handled time by the agents within the given time.
            </p>
          </TooltipContent>
        </TooltipUI>
      </TooltipProvider>
    </div>
  </CardHeader>
);

const AverageHandlingTimeTrendWidget: React.FC<AverageHandlingTimeTrendWidgetProps> = ({
  data,
  loading,
  error,
  rangeType = 'weekly'
}) => {
  const { theme } = useDashboard();

  const chartData = useMemo(() => {
    const rawData = data.value as HandlingTimeDataPoint[];
    if (!Array.isArray(rawData) || rawData.length === 0) {
      return [];
    }

    return rawData;
  }, [data.value]);

  const hasData = Array.isArray(chartData) && chartData.length > 0;

  const renderContent = () => {
    if (loading) return <Skeleton className="w-full h-[300px] rounded-none" />;
    if (error) return <div className="text-red-500 text-center py-10">{error}</div>;
    if (!hasData) return (
      <div className="flex items-center justify-center h-[300px] text-sm text-muted-foreground dark:text-white/60">
        <div className="flex flex-col items-center gap-2">
          <span>No call records were found</span>
        </div>
      </div>
    );
    return <ChartContent data={chartData} theme={theme} rangeType={rangeType} />;
  };

  return (
    <Card className="w-full overflow-hidden min-h-[300px] dark:border-0">
      <WidgetHeader />
      <CardContent className="p-0">
        <div className="relative overflow-hidden">
          {renderContent()}
        </div>
      </CardContent>
    </Card>
  );
};

export default AverageHandlingTimeTrendWidget;
